<template>
  <section>
    <Tabs>
      <TabsHeader tabs-header-class="atb-bg-white">
        <Tab
          v-for="(tab, index) in tabs"
          :key="'Tab' + tab.id"
          :index="index"
          :value="selectedTab"
          :closable="tab.name !== 'All Roles'"
          @tabClick="(index: number) => tabChange(index)"
          @close="(index: number) => closeTab(index, tab)"
        >
          {{ tab.name }}
          <sup v-if="getTabDirtyStatus(tab.id)">*</sup>
        </Tab>
      </TabsHeader>
      <TabsBody>
        <TabPanelItem
          v-for="(tab, index) in tabs"
          :key="tab.id"
        >
          <keep-alive
            :key="'keepAlive' + index"
            v-if="index === 0"
          >
            <RolesList
              ref="rolesListRef"
              :key="'roleList' + tab.id"
              v-show="selectedTab === index"
              @create="openCreateFormTab"
              @edit="openEditFormTab"
              @deleteById="handleDeleteRole"
            />
          </keep-alive>
          <keep-alive
            :key="'keepAlive' + tab.id"
            v-else
          >
            <RoleDetails
              :key="'roleDetail' + tab.id"
              v-show="selectedTab === index"
              :current-role-id="tab.id"
              :tab-name="tab.name"
              @close-current-tab="closeCurrentTab"
              @deleteById="handleDeleteRole"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
  </section>
  <RoleDelete
    @delete="deleteRole"
    :is-used="0"
    :entity="'Role'"
    ref="deleteModalRef"
  />
</template>

<script setup lang="ts">
  import { nextTick, Ref, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import RolesList from '@/app/features/admin/roles/components/RolesList.vue';
  import { ModalActions } from '@/app/shared/types/modal-actions.interface';
  import { useRoleStore } from '@/app/features/admin/roles/role-store';
  import { TabHeader } from '@/app/shared/types/tab-header.interface';
  import RoleDelete from '@/app/features/ams/shared/DeleteModal.vue';
  import RoleDetails from '@/app/features/admin/roles/components/RoleDetails.vue';
  import { AdminAddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const roleStore = useRoleStore();
  const alertStore = useAlertStore();
  const rolesListRef = ref<InstanceType<typeof RolesList>[]>([]);
  const { selectedTab, tabs } = storeToRefs(roleStore);
  const deleteModalRef: Ref<ModalActions | null> = ref(null);
  const roleIdToDelete = ref<number | string | null>(null);

  const getTabDirtyStatus = (roleId: string | number) => {
    let dirtyFlag = false;
    let dirtyTab = roleStore.roleDetails.get(roleId)?.dirtyTab;
    if (typeof dirtyTab !== 'undefined') {
      for (const [, dirtyTabValue] of dirtyTab ?? new Map().entries()) {
        if (dirtyTabValue) {
          dirtyFlag = true;
        }
      }
    }
    return dirtyFlag;
  };

  const tabChange = (index: number) => {
    selectedTab.value = index;
  };

  const closeCurrentTab = (index: number) => {
    roleStore.closeTab(index);
    rolesListRef.value[0]?.refreshRoles();
    if (index === 1 && index <= selectedTab.value) {
      selectedTab.value -= 1;
      nextTick(() => {
        rolesListRef.value[0]?.refreshRoles();
      });
    } else if (index <= selectedTab.value) {
      rolesListRef.value[0]?.refreshRoles();
    }
  };

  const closeTab = (index: number, tab: TabHeader) => {
    roleStore.closeTab(tab.id);
  };

  const openCreateFormTab = async () => {
    await roleStore.createRoleTab({
      id: -1,
      name: 'New Role',
      isNew: true,
      dirtyTab: new Map<string, boolean>(),
    });
  };

  const openEditFormTab = async ({
    id,
    name,
  }: AdminAddTabEvent): Promise<void> => {
    await roleStore.createRoleTab({
      id: id,
      name: name,
      isNew: false,
      dirtyTab: new Map<string, boolean>(),
    });
  };

  const handleDeleteRole = (roleId: number | string) => {
    roleIdToDelete.value = roleId;
    deleteModalRef.value?.changeModalVisibility();
  };

  const deleteRole = async () => {
    if (roleIdToDelete.value == null) return;
    await roleStore.deleteRole(roleIdToDelete.value);
    rolesListRef.value[0]?.refreshRoles();
    closeCurrentTab(roleIdToDelete.value as number);
    alertStore.showNotification(t('roleDeleted'), t('Role'), Motif.SUCCESS);
  };
</script>

<style scoped>
  .role-page {
    padding: 20px;
  }

  h1 {
    color: #333;
    margin-bottom: 16px;
  }
</style>
